import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CirclePlus, FileDownIcon } from "lucide-react";
import Button from "../../../../../../../../../components/shared/Button";
import RemoveButton from "./RemoveButton";
import { useScheduleContext } from "../../../../../../hooks/context/useScheduleContext";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useAccess } from "../../../../../../../../../utils/hooks/access";
import { cn } from "../../../../../../../../../lib/utils";

type ActionsType = {
    publish: () => Promise<void>;
    saveDraft: () => Promise<void>;
    saveTemplate: (value?: string | undefined) => Promise<boolean | void>;
    remove: () => void,
    createRequest: () => Promise<void>;
    unassignedShiftSubmit: () => Promise<void>;
    close: () => void;
    openSelectTemplateModal: (item?: any) => void;
    loading: boolean;
}

function Actions({
    publish,
    saveDraft,
    saveTemplate,
    remove,
    createRequest,
    unassignedShiftSubmit,
    close,
    openSelectTemplateModal,
    loading
}: ActionsType) {
    const { t } = useTranslation();
    const { type, selectedCellOrEvent } = useScheduleContext();

    const { canEdit, canCreate, canDelete, canCreateUnassignedShift, canEditUnassignedShift, canDeleteUnassignedShift } = usePermissions();
    const { hasAccess: canCreateRequest } = useAccess(type === "home" ? "request.canCreate" : "");

    const isCreating = selectedCellOrEvent?.isCreating;
    const isUnassigned = selectedCellOrEvent?.isUnassignedShift;
    const isTemplate = selectedCellOrEvent?.isTemplate;

    const content = useMemo(() => {
        if (isUnassigned) {
            return (
                <div className="tw-flex tw-items-center tw-gap-3 tw-justify-end">
                    {((isCreating && canCreateUnassignedShift) || (!isCreating && canEditUnassignedShift)) && (
                        <Button
                            type="button"
                            onClick={unassignedShiftSubmit}
                            loading={loading}
                            disabled={loading}
                        >
                            {isCreating ? t("create") : t("edit")}
                        </Button>
                    )}

                    <RemoveButton
                        loading={loading}
                        onClick={(isCreating || !canDeleteUnassignedShift) ? close : remove}
                    />
                </div>
            );
        }

        if (
            (["template", "profile", "my-profile"].includes(type) || isTemplate) &&
            ((isCreating && canCreate) || (!isCreating && canEdit) || canDelete)
        ) {
            return (
                <div className="tw-flex tw-items-center tw-gap-3 tw-justify-end">
                    {((isCreating && canCreate) || (!isCreating && canEdit)) && (
                        <Button
                            type="button"
                            onClick={() => saveTemplate()}
                            loading={loading}
                            disabled={loading}
                        >
                            {isCreating ? t("create") : t("edit")}
                        </Button>
                    )}

                    <RemoveButton
                        loading={loading}
                        onClick={(isCreating || !canDelete) ? close : remove}
                    />
                </div>
            );
        }

        if (["home", "team"].includes(type)) {
            if ((isCreating && canCreate) || (!isCreating && canEdit) || canDelete) {
                return (
                    <div className={cn(
                        "tw-grid tw-gap-4 tw-items-center",
                        type === "home" ? "tw-grid-cols-[1fr_auto]" : "tw-grid-cols-[1fr_1fr_auto]"
                    )}>
                        {((isCreating && canCreate) || (!isCreating && canEdit)) && (
                            <>
                                {type !== "home" && (
                                    <Button
                                        type="button"
                                        size="lg"
                                        onClick={publish}
                                        loading={loading}
                                        disabled={loading}
                                    >
                                        {t("publish")}
                                    </Button>
                                )}

                                <Button
                                    className="tw-text-primary"
                                    variant="secondary"
                                    type="button"
                                    size="lg"
                                    onClick={saveDraft}
                                    loading={loading}
                                    disabled={loading}
                                >
                                    {t("save-draft")}
                                </Button>
                            </>
                        )}

                        <div className="tw-flex tw-items-center tw-gap-4 tw-w-full">
                            <RemoveButton
                                loading={loading}
                                size="lg"
                                onClick={(isCreating || !canDelete) ? close : remove}
                            />

                            {canCreate && type === "team" && (
                                <Button
                                    type="button"
                                    className="tw-w-[80%] tw-text-primary"
                                    variant="secondary"
                                    size="lg"
                                    onClick={openSelectTemplateModal}
                                    loading={loading}
                                    disabled={loading}
                                >
                                    <FileDownIcon className="tw-text-primary" />
                                    {t("save-as-template")}
                                </Button>
                            )}
                        </div>
                    </div>
                );
            }

            if (canCreateRequest && ((isCreating && !canCreate) || (!isCreating && !canEdit) || canDelete)) {
                return (
                    <div className="tw-flex tw-items-center tw-gap-3 tw-justify-end">
                        {(canCreateRequest && ((isCreating && !canCreate) || (!isCreating && !canEdit))) && (
                            <Button
                                type="button"
                                className="tw-text-primary"
                                variant="secondary"
                                size="lg"
                                onClick={createRequest}
                                loading={loading}
                                disabled={loading}
                            >
                                <CirclePlus className="tw-text-primary" />
                                {t("create-request")}
                            </Button>
                        )}

                        <RemoveButton
                            loading={loading}
                            size="lg"
                            onClick={(isCreating || !canDelete) ? close : remove}
                        />
                    </div>
                );
            }
        }

        return null;
    }, [
        loading,
        type,
        isCreating,
        isUnassigned,
        isTemplate,
        canCreate,
        canEdit,
        canDelete,
        canCreateRequest,
        canCreateUnassignedShift,
        canEditUnassignedShift,
        canDeleteUnassignedShift,
        publish,
        saveDraft,
        saveTemplate,
        remove,
        createRequest,
        unassignedShiftSubmit,
        close,
        openSelectTemplateModal,
        t,
    ]);

    return content;
}

export default Actions;
